/**
 * لوحة تحكم المستفيد — نقطة دخول واحدة بعد تسجيل الدخول.
 * Route: /my-dashboard
 * يعرض كرتي الطلبات (عقود الصيانة + الأراضي) بعدد وحالة، وروابط تقديم طلب جديد.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isAuthenticated } from "../../../services/authService";
import * as donorService from "../../../services/donorService";
import { listSupplyRequestsByDonor, type SupplyRequestListItem } from "../../../services/mosqueDonationService";
import { listHygieneSupplyRequestsByDonor, type HygieneSupplyRequestListItem } from "../../../services/hygieneDonationService";
import { listDonationRequests, type DonationRequestItem } from "../../../services/donationRequestService";
import type { DonorRow } from "../../../types/donor";
import Breadcrumbs, { type Crumb } from "../../../components/navigation/Breadcrumbs";
import { asErr } from "../../../utils/apiHelpers";

type StatusKey = "draft" | "progress" | "submitted" | "approved" | "reject" | "other";
const STATUS_LABELS: Record<StatusKey, string> = {
  draft: "مسودة",
  progress: "قيد المعالجة",
  submitted: "تم الإرسال",
  approved: "معتمد",
  reject: "مرفوض",
  other: "غير معروف",
};

function classifyState(state: string | null | undefined): StatusKey {
  const s = (state || "").toLowerCase();
  if (s.includes("draft")) return "draft";
  if (s.includes("progress")) return "progress";
  if (s.includes("submitted")) return "submitted";
  if (s.includes("approved") || s.includes("done")) return "approved";
  if (s.includes("reject")) return "reject";
  return "other";
}

function aggregateByStatus<T extends { state?: string | null }>(items: T[]): Record<StatusKey, number> {
  const counts: Record<StatusKey, number> = {
    draft: 0,
    progress: 0,
    submitted: 0,
    approved: 0,
    reject: 0,
    other: 0,
  };
  items.forEach((item) => {
    const key = classifyState(item.state);
    counts[key]++;
  });
  return counts;
}

export default function BeneficiaryDashboardPage() {
  const navigate = useNavigate();
  const loggedIn = isAuthenticated();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceTotal, setMaintenanceTotal] = useState(0);
  const [maintenanceByStatus, setMaintenanceByStatus] = useState<Record<StatusKey, number>>({
    draft: 0,
    progress: 0,
    submitted: 0,
    approved: 0,
    reject: 0,
    other: 0,
  });
  const [landsTotal, setLandsTotal] = useState(0);
  const [landsByStatus, setLandsByStatus] = useState<Record<StatusKey, number>>({
    draft: 0,
    progress: 0,
    submitted: 0,
    approved: 0,
    reject: 0,
    other: 0,
  });
  const [hygieneTotal, setHygieneTotal] = useState(0);
  const [hygieneByStatus, setHygieneByStatus] = useState<Record<StatusKey, number>>({
    draft: 0,
    progress: 0,
    submitted: 0,
    approved: 0,
    reject: 0,
    other: 0,
  });

  const crumbs: Crumb[] = [
    { label: "الرئيسية", to: "/" },
    { label: "لوحة المؤشرات", to: null },
  ];

  useEffect(() => {
    if (!loggedIn) {
      const returnUrl = encodeURIComponent("/my-dashboard");
      navigate(`/benefaction/login?returnUrl=${returnUrl}`, { replace: true });
      return;
    }

    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const allDonors = (await donorService.listMine()) as DonorRow[];
        const ownerDonors = donorService.scopeDonorsToCurrentUser(allDonors);
        if (!mounted) return;

        let allMaintenance: SupplyRequestListItem[] = [];
        let allLands: DonationRequestItem[] = [];
        let allHygiene: HygieneSupplyRequestListItem[] = [];

        for (const donor of ownerDonors) {
          const token = donor.donorIdToken ?? (donor as any).donorIdToken;
          if (!token) continue;
          try {
            const [maintenanceRes, hygieneRes] = await Promise.all([
              listSupplyRequestsByDonor({ DonorIdToken: token, PageNo: 1, PageSize: 500 }),
              listHygieneSupplyRequestsByDonor({ DonorIdToken: token, PageNo: 1, PageSize: 500 }).catch(() => ({ data: [] as HygieneSupplyRequestListItem[] })),
            ]);
            if (mounted) {
              allMaintenance = allMaintenance.concat(maintenanceRes.data ?? []);
              allHygiene = allHygiene.concat(hygieneRes.data ?? []);
            }
          } catch (_) {
            // per-donor errors: continue with others
          }
        }

        for (const donor of allDonors) {
          const token = donor.donorIdToken ?? (donor as any).donorIdToken;
          if (!token) continue;
          try {
            const landReqs = await listDonationRequests(token, undefined);
            if (mounted) {
              allLands = allLands.concat(landReqs ?? []);
            }
          } catch (_) {
            // per-donor errors: continue with others
          }
        }

        if (!mounted) return;
        const uniqueLandRequests = Array.from(
          new Map(allLands.map((row) => [row.tokenReqId || `${row.code}-${row.landId}`, row])).values()
        );
        setMaintenanceTotal(allMaintenance.length);
        setMaintenanceByStatus(aggregateByStatus(allMaintenance));
        setLandsTotal(uniqueLandRequests.length);
        setLandsByStatus(aggregateByStatus(uniqueLandRequests));
        setHygieneTotal(allHygiene.length);
        setHygieneByStatus(aggregateByStatus(allHygiene));
      } catch (e) {
        if (mounted) setError(asErr(e, "تعذر تحميل بيانات الطلبات").message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loggedIn, navigate]);

  if (!loggedIn) return null;

  const renderStatusSummary = (byStatus: Record<StatusKey, number>) => {
    const entries = (Object.entries(byStatus) as [StatusKey, number][]).filter(([, n]) => n > 0);
    if (entries.length === 0) return <span className="text-neutral-500 text-sm">لا توجد طلبات</span>;
    return (
      <ul className="mt-2 space-y-1 text-sm text-neutral-600">
        {entries.map(([key, n]) => (
          <li key={key}>
            {STATUS_LABELS[key]}: <span className="font-medium text-neutral-800">{n}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50" dir="rtl">
      <div className="mx-auto max-w-4xl px-4 py-8">
        <Breadcrumbs items={crumbs} />

        <h1 className="mt-4 text-2xl font-bold text-neutral-900 text-center">لوحة المؤشرات</h1>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-6 text-neutral-500">جاري تحميل الطلبات...</div>
        ) : (
          <>
            <section className="mt-8">
              <h2 className="text-lg font-semibold text-neutral-800 mb-4 text-center">طلباتي</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/my-requests?tab=maintenance"
                  className="group block rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm hover:border-sa-300 hover:shadow-lg hover:bg-sa-50/30 hover:-translate-y-0.5 transition-all duration-200 text-right"
                >
                  <h3 className="text-base font-semibold text-neutral-800">طلبات عقود الصيانة</h3>
                  <p className="mt-2 text-2xl font-bold text-sa-600">{maintenanceTotal}</p>
                  <div className="mt-3">{renderStatusSummary(maintenanceByStatus)}</div>
                  <p className="mt-3 text-sa-600 text-sm font-medium group-hover:text-sa-700 transition-colors">عرض الطلبات ←</p>
                </Link>
                <Link
                  to="/my-requests?tab=hygiene"
                  className="group block rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm hover:border-sa-300 hover:shadow-lg hover:bg-sa-50/30 hover:-translate-y-0.5 transition-all duration-200 text-right"
                >
                  <h3 className="text-base font-semibold text-neutral-800">طلبات أعمال النظافة والتعقيم</h3>
                  <p className="mt-2 text-2xl font-bold text-sa-600">{hygieneTotal}</p>
                  <div className="mt-3">{renderStatusSummary(hygieneByStatus)}</div>
                  <p className="mt-3 text-sa-600 text-sm font-medium group-hover:text-sa-700 transition-colors">عرض الطلبات ←</p>
                </Link>
                <Link
                  to="/my-requests?tab=lands"
                  className="group block rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm hover:border-sa-300 hover:shadow-lg hover:bg-sa-50/30 hover:-translate-y-0.5 transition-all duration-200 text-right"
                >
                  <h3 className="text-base font-semibold text-neutral-800">طلبات الأراضي المتاحة</h3>
                  <p className="mt-2 text-2xl font-bold text-sa-600">{landsTotal}</p>
                  <div className="mt-3">{renderStatusSummary(landsByStatus)}</div>
                  <p className="mt-3 text-sa-600 text-sm font-medium group-hover:text-sa-700 transition-colors">عرض الطلبات ←</p>
                </Link>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold text-neutral-800 mb-4">تقديم طلب جديد</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link
                  to="/benefaction/lands"
                  className="flex items-center justify-between min-h-[44px] rounded-xl border border-neutral-200 bg-white px-5 py-4 shadow-sm hover:border-sa-300 hover:bg-sa-50 hover:shadow-md transition-all duration-200"
                >
                  <span className="font-medium text-neutral-800">الأراضي المتاحة لبناء مسجد</span>
                  <span className="text-sa-600 text-sm hover:text-sa-700">الدخول ←</span>
                </Link>
                <Link
                  to="/service/mosque-contribution"
                  className="flex items-center justify-between min-h-[44px] rounded-xl border border-neutral-200 bg-white px-5 py-4 shadow-sm hover:border-sa-300 hover:bg-sa-50 hover:shadow-md transition-all duration-200"
                >
                  <span className="font-medium text-neutral-800">فرص عقود الصيانة</span>
                  <span className="text-sa-600 text-sm hover:text-sa-700">الدخول ←</span>
                </Link>
                <Link
                  to="/mosque/donation-opportunities?catAId=900001"
                  className="flex items-center justify-between min-h-[44px] rounded-xl border border-neutral-200 bg-white px-5 py-4 shadow-sm hover:border-sa-300 hover:bg-sa-50 hover:shadow-md transition-all duration-200"
                >
                  <span className="font-medium text-neutral-800">فرص أعمال النظافة والتعقيم</span>
                  <span className="text-sa-600 text-sm hover:text-sa-700">الدخول ←</span>
                </Link>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
